import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

const badgeVariants = cva(
	'inline-flex items-center justify-center gap-0 overflow-hidden text-xs font-semibold whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
	{
		variants: {
			variant: {
				// --- Shadcn solid badges (node 136:1178) ---
				default:
					'bg-primary text-primary-foreground hover:bg-primary/80',
				secondary:
					'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				outline:
					'border border-border bg-background text-foreground hover:bg-muted',
				destructive:
					'bg-destructive text-destructive-foreground hover:bg-destructive/80',
				// --- Status / semantic badges (node 5717:23608) ---
				success:
					'bg-(--color-success-light) text-(--color-success) hover:bg-green-200',
				warning:
					'bg-(--color-warning-light) text-(--color-warning) hover:bg-orange-200',
				info:
					'bg-blue-100 text-blue-600 hover:bg-blue-200',
				muted:
					'bg-secondary text-muted-foreground hover:bg-secondary/80',
				// --- Special badges (node 5717:23608) ---
				logo:
					'bg-secondary hover:bg-secondary/80',
				avatar:
					'bg-secondary text-secondary-foreground hover:bg-secondary/80',
			},
			size: {
				md: 'h-6 px-3 py-1',
				sm: 'h-5 px-2 py-0.5',
			},
			rounded: {
				true:  'rounded-full',
				false: 'rounded-md',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'md',
			rounded: true,
		},
	}
)

export interface BadgeProps
	extends React.HTMLAttributes<HTMLSpanElement>,
		VariantProps<typeof badgeVariants> {
	rounded?: boolean
	/** For variant="avatar" */
	avatarSrc?: string
	avatarAlt?: string
	/** For variant="logo" */
	logoSrc?: string
	logoAlt?: string
	/** Makes the logo fill the entire badge (no padding) */
	logoFill?: boolean
}

function Badge({
	className,
	variant,
	size,
	rounded = true,
	avatarSrc,
	avatarAlt,
	logoSrc,
	logoAlt,
	logoFill = false,
	children,
	...props
}: BadgeProps) {
	const avatarSize = size === 'sm' ? 'size-4' : 'size-5'
	const avatarPadding = size === 'sm' ? 'pl-0.5 pr-2 gap-1' : 'pl-0.5 pr-3 gap-0.5'

	if (variant === 'avatar') {
		return (
			<span
				className={cn(badgeVariants({ variant, size, rounded }), avatarPadding, className)}
				{...props}
			>
				{avatarSrc && (
					<img
						src={avatarSrc}
						alt={avatarAlt ?? ''}
						className={cn('shrink-0 rounded-full object-cover', avatarSize)}
					/>
				)}
				{children}
			</span>
		)
	}

	if (variant === 'logo') {
		return (
			<span
				className={cn(badgeVariants({ variant, size, rounded }), logoFill && 'p-0', className)}
				{...props}
			>
				{logoSrc ? (
					<img
						src={logoSrc}
						alt={logoAlt ?? ''}
						className={logoFill ? 'h-full w-auto object-cover' : 'h-4 w-14 object-contain'}
					/>
				) : (
					children
				)}
			</span>
		)
	}

	return (
		<span
			className={cn(badgeVariants({ variant, size, rounded }), className)}
			{...props}
		>
			{children}
		</span>
	)
}

export { Badge, badgeVariants }
